/**
 * In-memory hand-off for the one-time MFA recovery codes shown right after
 * enrollment.
 *
 * The codes are irrecoverable and must never touch persistent storage or
 * telemetry. They are shown on a dedicated full-screen page
 * (`/auth/mfa-recovery-codes`) that lives OUTSIDE `ProtectedRoute` — an
 * involuntary session-expiry redirect can't unmount it, and it has no app nav
 * chrome to click away through. We pass the codes from the settings card to
 * that page through this module-level variable rather than:
 *   - React Router location state — it serializes into `window.history.state`,
 *     which survives reloads (a persistence we explicitly do not want), or
 *   - sessionStorage — persistent and readable by other code.
 *
 * The page CONSUMES the codes on mount (clears the module var once they're in
 * component state), so navigating away and revisiting the public route — or a
 * different account signing in within the tab — can't re-expose them. `peek`
 * (not clear-on-read) keeps the read resilient to React StrictMode's
 * double-invoked `useState` initializer; the clear happens in a mount effect.
 */

/**
 * `generatedAt` rides along with the codes: the printed / downloaded sheet
 * stamps the issue date, and it must be the SERVER's timestamp for the set —
 * not the moment the user happened to hit Print.
 */
let pending: { codes: string[]; generatedAt?: string } | null = null;

/** Stage the codes for the recovery-codes page. */
export function stashRecoveryCodes(codes: string[], generatedAt?: string): void {
  pending = { codes, generatedAt };
}

/** Read the staged codes without clearing them (StrictMode-safe). */
export function peekRecoveryCodes(): string[] | null {
  return pending?.codes ?? null;
}

/** The server-issued timestamp for the staged set, for the offline copy. */
export function peekRecoveryCodesGeneratedAt(): string | undefined {
  return pending?.generatedAt;
}

export function clearRecoveryCodes(): void {
  pending = null;
}

// The cross-login "pending acknowledgement" marker that used to live here has
// been removed. It existed only because enrollment revoked the session, forcing
// the user's "I've saved my codes" confirmation to survive a re-login. The
// session now survives enrollment, so the recovery-codes page acknowledges
// directly against the live session and nothing needs to be carried across.
